/**
 * Authorization Code flow with PKCE.
 *
 * No client secret exists — a public client cannot keep one — so PKCE is what
 * stops an intercepted authorization code being redeemed by anyone else. The
 * verifier never leaves this browser; only its hash does.
 */

const VERIFIER_KEY = 'qim.pkce.verifier'
const STATE_KEY = 'qim.pkce.state'

const base64Url = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** 32 bytes of CSPRNG output, base64url encoded — 43 characters, within spec. */
export const randomUrlSafe = (bytes = 32): string =>
  base64Url(crypto.getRandomValues(new Uint8Array(bytes)))

export const challengeFor = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

export interface PendingAuth {
  readonly verifier: string
  readonly state: string
}

/**
 * Kept in sessionStorage rather than localStorage: the flow is meaningful only
 * within one tab's session, and a leftover verifier in localStorage would
 * outlive the sign-in it belongs to.
 */
export const rememberPending = (storage: Storage, pending: PendingAuth): void => {
  storage.setItem(VERIFIER_KEY, pending.verifier)
  storage.setItem(STATE_KEY, pending.state)
}

/**
 * Reads and *clears* the pending flow. Single use: a verifier that survives its
 * exchange could be replayed against a second intercepted code.
 */
export const takePending = (storage: Storage): PendingAuth | undefined => {
  const verifier = storage.getItem(VERIFIER_KEY)
  const state = storage.getItem(STATE_KEY)
  storage.removeItem(VERIFIER_KEY)
  storage.removeItem(STATE_KEY)
  if (verifier === null || state === null) return undefined
  return { verifier, state }
}

export type CallbackResult =
  | { readonly kind: 'code'; readonly code: string }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'none' }

/**
 * Reads the callback parameters and checks `state`.
 *
 * A mismatched state means the response did not originate from a flow this tab
 * started, which is what makes it a CSRF defence rather than decoration. It is
 * treated as an error, never ignored.
 */
export const readCallback = (search: string, expectedState: string | undefined): CallbackResult => {
  const params = new URLSearchParams(search)
  const error = params.get('error')
  if (error !== null) {
    return { kind: 'error', message: params.get('error_description') ?? error }
  }

  const code = params.get('code')
  if (code === null) return { kind: 'none' }

  const state = params.get('state')
  if (expectedState === undefined || state !== expectedState) {
    return { kind: 'error', message: 'Sign-in state did not match. Start again.' }
  }

  return { kind: 'code', code }
}

export interface AuthConfig {
  readonly domain: string
  readonly clientId: string
  readonly redirectUri: string
  readonly providerName: string
}

export const authorizeUrl = (config: AuthConfig, challenge: string, state: string): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: config.redirectUri,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    // Straight to Identity Center — the pool has no other provider, so a chooser
    // would be a dead end.
    identity_provider: config.providerName,
  })
  return `${config.domain}/oauth2/authorize?${params.toString()}`
}

export interface TokenSet {
  /**
   * The **id** token, not the access token.
   *
   * API Gateway's JWT authorizer validates `aud`, and the application needs
   * `email` — a Cognito access token carries neither. Sending the access token
   * produces a 401 that looks like a permissions problem.
   */
  readonly idToken: string
  readonly expiresAt: number
}

export const exchangeCode = async (
  config: AuthConfig,
  code: string,
  verifier: string,
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<TokenSet> => {
  const response = await fetchImpl(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }).toString(),
  })

  if (!response.ok) {
    throw new Error(`Token exchange failed (${String(response.status)})`)
  }

  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null) throw new Error('Token response was not an object')
  const idToken = (body as Record<string, unknown>)['id_token']
  const expiresIn = (body as Record<string, unknown>)['expires_in']
  if (typeof idToken !== 'string') throw new Error('Token response carried no id_token')

  return {
    idToken,
    expiresAt: now() + (typeof expiresIn === 'number' ? expiresIn : 900) * 1000,
  }
}
